const PANEL_SELECTOR = '[data-monthly-postcard-month]'
const INITIALIZED = 'data-monthly-postcard-initialized'
const bindings = new WeakMap()
const OCTOBER_RUMBLE_MS = 2800
const OCTOBER_REVEAL_MS = 8600
const EXIT_MS = 500
const REVEAL_CROSSFADE_MS = 1200
const PAGE_STAGE_CLASS = 'monthly-postcard-page-stage'

const MONTH_MOTION = {
    '01': {name: 'frost-rise', rumbleMs: 900, revealMs: 5500},
    '02': {name: 'letter-gather', rumbleMs: 850, revealMs: 5400},
    '03': {name: 'spring-bloom', rumbleMs: 850, revealMs: 5400},
    '04': {name: 'rain-sheet', rumbleMs: 760, revealMs: 5200},
    '05': {name: 'garden-spiral', rumbleMs: 870, revealMs: 5600},
    '06': {name: 'sunburst', rumbleMs: 820, revealMs: 5400},
    '07': {name: 'summer-spark', rumbleMs: 950, revealMs: 5700},
    '08': {name: 'sun-turn', rumbleMs: 900, revealMs: 5600},
    '09': {name: 'orchard-drop', rumbleMs: 800, revealMs: 5400},
    '10': {name: 'october', rumbleMs: OCTOBER_RUMBLE_MS, revealMs: OCTOBER_REVEAL_MS},
    '11': {name: 'leaf-fall', rumbleMs: 820, revealMs: 5500},
    '12': {name: 'paper-fold', rumbleMs: 900, revealMs: 5600},
}

function prefersReducedMotion(overlay) {
    return overlay.dataset.reducedMotion === 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function postcardMotion(month) {
    const overrides = {
        '2027-11': {name: 'harvest-gather', rumbleMs: 900, revealMs: 5600},
        '2027-12': {name: 'paper-screen', rumbleMs: 900, revealMs: 5700},
        '2028-01': {name: 'berry-drift', rumbleMs: 900, revealMs: 5500},
        '2028-02': {name: 'letter-send', rumbleMs: 880, revealMs: 5500},
    }

    return overrides[month?.slice(0, 7)] ?? MONTH_MOTION[month?.slice(5, 7)] ?? MONTH_MOTION['10']
}

export function currentNewYorkMonth(date = new Date()) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
    }).formatToParts(date)
    const year = parts.find((part) => part.type === 'year')?.value
    const month = parts.find((part) => part.type === 'month')?.value

    return `${year}-${month}-01`
}

export function buildPostcardParticles(layer, theme, october = false) {
    const winter = ['#b8d0ce', '#d3bcca', '#e5dcb0', '#a3bab5']
    const spring = ['#d5a8bb', '#b7d1b2', '#e8ca92', '#d6b3cb']
    const autumn = ['#dfa88e', '#d6b1b7', '#abc3a6', '#e5c083']
    const harvest = ['#b87d62', '#c7a06d', '#a0a477', '#d2b28b']
    const palette = ['winter', 'new-year'].includes(theme) ? winter : ['spring', 'rain', 'garden', 'hearts'].includes(theme) ? spring : theme === 'harvest' || theme === 'orchard' ? harvest : autumn

    const count = october ? 48 : 30

    for (let index = 0; index < count; index += 1) {
        const particle = document.createElement('span')
        const angle = (index / count) * Math.PI * 2
        const distance = october ? 170 + (index % 6) * 45 : 115 + (index % 5) * 30

        particle.className = 'nurse-monthly-postcard__particle'
        particle.style.setProperty('--particle-x', `${Math.round(Math.cos(angle) * distance)}px`)
        particle.style.setProperty('--particle-y', `${Math.round(Math.sin(angle) * distance)}px`)
        particle.style.setProperty('--particle-turn', `${(index * 47) % 360}deg`)
        particle.style.setProperty('--particle-delay', `${(index % 6) * (october ? 18 : 24)}ms`)
        particle.style.setProperty('--particle-size', `${(october ? 10 : 8) + (index % 4) * 3}px`)
        particle.style.setProperty('--particle-color', palette[index % palette.length])
        layer.appendChild(particle)
    }
}

export async function postPostcardEvent(endpoint, fetchImplementation = globalThis.fetch) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    if (!endpoint || !csrfToken || typeof fetchImplementation !== 'function') {
        return null
    }

    try {
        const response = await fetchImplementation(endpoint, {
            method: 'POST',
            credentials: 'same-origin',
            keepalive: true,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
            },
        })

        if (!response.ok) {
            return null
        }

        return 204 === response.status ? true : response.json()
    } catch {
        return null
    }
}

// Play the licensed acoustic trio when the postcard appears, and fade it on mute or close.
function startAutumnJazz(source, onEnded) {
    if (!source) return null

    const audio = new Audio(source)
    audio.volume = 0
    let fadeTimer = null
    let stopped = false

    const fade = (target, duration, after = () => {}) => {
        if (fadeTimer) clearInterval(fadeTimer)
        const initial = audio.volume
        const started = performance.now()
        const step = () => {
            const progress = Math.min((performance.now() - started) / duration, 1)
            audio.volume = initial + (target - initial) * progress
            if (progress === 1) {
                clearInterval(fadeTimer)
                fadeTimer = null
                after()
            }
        }
        fadeTimer = setInterval(step, 40)
        step()
    }

    const stop = () => {
        if (stopped) return
        stopped = true
        fade(0, 800, () => {
            audio.pause()
            audio.removeAttribute('src')
            audio.load()
        })
    }

    audio.addEventListener('ended', () => {
        if (stopped) return
        stop()
        onEnded()
    }, {once: true})
    audio.addEventListener('error', () => {
        if (stopped) return
        stop()
        onEnded()
    }, {once: true})
    void audio.play().then(() => {
        if (!stopped) fade(0.16, 1800)
    }).catch(() => {
        if (stopped) return
        stop()
        onEnded()
    })

    return stop
}

// Decorative art for the opening scene; the approved postcard itself stays untouched.
function addOctoberScenery(stage) {
    const pumpkin = `<svg viewBox="0 0 180 150" aria-hidden="true"><g stroke="#745d51" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M88 42c-7-18 0-29 10-34 1 13-1 24-10 34Z" fill="#849477"/><path d="M88 40C55 23 28 48 29 84c0 44 27 62 61 62s65-18 62-62c-2-37-33-61-64-44Z" fill="#d99a72"/><path d="M85 42c-27 15-26 88 0 103M100 41c28 15 30 87 3 103M91 42c-8 30-7 72 0 103" fill="none" opacity=".6"/><path d="M91 37c16-18 34-17 45-10-14 2-29 10-45 10Z" fill="#a5b08d"/></g></svg>`
    const ghost = `<svg viewBox="0 0 120 135" aria-hidden="true"><path d="M23 102V58c0-29 16-47 37-47s38 18 38 47v50c-10-8-17-8-26 0-8 8-16 8-24 0-8-8-17-8-25-6Z" fill="#fffaf0" stroke="#958980" stroke-width="2" stroke-linejoin="round"/><path d="M40 43c9-17 26-23 42-14" fill="none" stroke="#d8cec0" stroke-width="3" stroke-linecap="round"/><ellipse cx="51" cy="65" rx="3" ry="4" fill="#615d5b"/><ellipse cx="74" cy="65" rx="3" ry="4" fill="#615d5b"/><path d="M56 79c3 4 8 4 12 0" fill="none" stroke="#a77878" stroke-width="2" stroke-linecap="round"/></svg>`

    stage.insertAdjacentHTML('beforeend', `
        <span class="monthly-postcard-page-stage__color monthly-postcard-page-stage__color--one"></span>
        <span class="monthly-postcard-page-stage__color monthly-postcard-page-stage__color--two"></span>
        <span class="monthly-postcard-page-stage__color monthly-postcard-page-stage__color--three"></span>
        <svg class="monthly-postcard-page-stage__swirls" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
            <g fill="none" stroke-linecap="round" opacity=".75">
                <path d="M-130 760C130 510 238 477 376 575c132 93 260 83 379-35 123-122 288-108 401 8 115 118 247 63 410-138" stroke="#d79a7c" stroke-width="55"/>
                <path d="M-120 826C129 622 260 601 391 687c152 100 284 46 395-65 130-131 294-93 397 17 122 129 276 91 414-33" stroke="#ac9ab1" stroke-width="31"/>
                <path d="M-94 150C168 48 281 47 346 173c51 97 197 132 289 71 151-101 271-49 347 43 111 132 309 123 555-86" stroke="#9fb298" stroke-width="38"/>
                <path d="M-94 81C156-1 304-18 411 93c74 77 170 92 246 55 146-70 277-30 378 82 128 139 315 79 494-52" stroke="#e6ba8c" stroke-width="25"/>
            </g>
        </svg>
        <svg class="monthly-postcard-page-stage__orchard" viewBox="0 0 340 220" aria-hidden="true"><g fill="none" stroke="#667d67" stroke-width="2" stroke-linecap="round"><path d="M-5 93C80 90 156 70 261 12M67 88c5-27 22-45 46-59M160 64c11-28 27-43 54-54M219 37c24 0 49 10 72 25"/><path d="M69 89c7 20 5 48-5 71M144 69c5 25-4 43-17 59M238 30c-3 26 2 50 15 71"/></g><g fill="#9fb190" stroke="#667d67" stroke-width="1.4"><path d="M112 29c-9-16-25-18-35-8 8 16 21 21 35 8ZM160 64c-7-21-27-27-39-18 7 17 21 23 39 18ZM214 10c16-15 34-12 42 1-11 12-25 15-42-1ZM290 62c-3-21-20-31-34-24 2 18 13 28 34 24Z"/></g><g fill="#c88368" stroke="#8b665d" stroke-width="1.5"><path d="M65 158c-13-10-27-3-29 11-4 21 14 35 29 28 17 7 34-8 30-28-3-15-18-21-30-11ZM127 126c-11-9-23-2-26 11-3 19 12 32 26 25 15 7 30-7 27-25-4-14-17-19-27-11ZM252 100c-11-8-25-3-28 11-4 20 13 34 28 27 16 7 33-8 29-27-4-14-18-20-29-11Z"/></g></svg>
        <span class="monthly-postcard-page-stage__pumpkin monthly-postcard-page-stage__pumpkin--left">${pumpkin}</span>
        <span class="monthly-postcard-page-stage__pumpkin monthly-postcard-page-stage__pumpkin--right">${pumpkin}</span>
        <span class="monthly-postcard-page-stage__ghost monthly-postcard-page-stage__ghost--left">${ghost}</span>
        <span class="monthly-postcard-page-stage__ghost monthly-postcard-page-stage__ghost--right">${ghost}</span>
        <svg class="monthly-postcard-page-stage__latte" viewBox="0 0 160 210" aria-hidden="true"><g fill="none" stroke="#77655d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M70 42c-18-20 14-24 0-40M102 46c-17-18 12-22 0-41" stroke="#b29c87"/><path d="M19 67h122l-13 123c-1 9-8 14-17 14H49c-9 0-16-5-17-14L19 67Z" fill="#f5e8d6"/><path d="M19 67c2-12 10-18 22-18h78c12 0 20 6 22 18H19Z" fill="#fbf6ea"/><path d="M30 105h100l-6 61H36l-6-61Z" fill="#b7866d"/><path d="M80 122c-10-6-17 1-15 12 1 11 6 16 15 15 9 1 14-4 15-15 2-11-5-18-15-12Z" fill="#eed5b5"/><path d="M80 122c-2-6 1-9 4-10M76 127c-5 6-5 11-1 17M84 126c5 6 5 12 1 18"/></g></svg>
    `)
}

function initializePostcard(panel) {
    const area = panel.querySelector('#nurse-performance-cards')
    const grid = area?.querySelector('.grid')
    const overlay = area?.querySelector('[data-monthly-postcard-overlay]')
    const particles = area?.querySelector('[data-monthly-postcard-particles]')
    const replay = area?.querySelector('[data-monthly-postcard-replay]')
    const closes = overlay?.querySelectorAll('[data-monthly-postcard-close]')
    const close = closes?.[0]
    const ambienceButton = overlay?.querySelector('[data-monthly-postcard-ambience]')
    const disclosure = panel.querySelector('[data-nurse-performance-disclosure]')
    const preview = panel.hasAttribute('data-monthly-postcard-preview')

    if (!area || !grid || !overlay || !particles || !replay || !close || !disclosure) {
        return
    }

    const previous = bindings.get(panel)
    if (previous?.replay === replay && previous?.overlay === overlay && previous?.disclosure === disclosure) {
        return
    }
    previous?.dispose()

    const listeners = new AbortController()
    panel.setAttribute(INITIALIZED, 'true')

    const motion = postcardMotion(panel.dataset.monthlyPostcardMonth)
    const october = motion.name === 'october'
    const {rumbleMs, revealMs} = motion

    if (october) area.classList.add('monthly-postcard-october')
    area.classList.add(`monthly-postcard-motion--${motion.name}`)
    area.style.setProperty('--postcard-rumble-duration', `${rumbleMs}ms`)
    area.querySelectorAll('[data-monthly-postcard-card]').forEach((card, index) => {
        card.style.setProperty('--postcard-delay', october ? '0ms' : `${index * 55}ms`)
    })
    particles.replaceChildren()
    buildPostcardParticles(particles, overlay.dataset.theme, october)

    let timers = []
    let playing = false
    let pending = false
    let pageStage = null
    let stopAmbience = null
    const overlayParent = overlay.parentNode
    const overlayNextSibling = overlay.nextSibling

    const clearTimers = () => {
        timers.forEach(clearTimeout)
        timers = []
    }

    const closePostcard = () => {
        document.documentElement.classList.remove('monthly-postcard-experience-active')
        stopAmbience?.()
        stopAmbience = null
        if (ambienceButton) {
            ambienceButton.setAttribute('aria-pressed', 'false')
            ambienceButton.textContent = 'Play soft jazz'
        }
        clearTimers()
        pageStage?.remove()
        pageStage = null
        document.documentElement.classList.remove('monthly-postcard-page-playing')
        document.body.classList.remove('monthly-postcard-page-melt')
        delete document.body.dataset.monthlyPostcardPageMotion
        document.body.style.removeProperty('--postcard-page-duration')
        area.classList.remove('monthly-postcard-rumble', 'monthly-postcard-burst')
        overlay.classList.remove('is-visible', 'is-exiting')
        overlay.hidden = true
        if (overlay.parentNode === document.body) {
            if (overlayParent.isConnected) overlayParent.insertBefore(overlay, overlayNextSibling)
            else overlay.remove()
        }
        grid.inert = false
        playing = false
        panel.removeAttribute('data-monthly-postcard-active')
        panel.dispatchEvent(new Event('monthly-postcard:closed'))
    }

    bindings.set(panel, {
        replay,
        overlay,
        disclosure,
        dispose: () => {
            listeners.abort()
            closePostcard()
        },
    })

    const dismissPostcard = () => {
        if (overlay.hidden || overlay.classList.contains('is-exiting')) return

        if (prefersReducedMotion(overlay)) {
            closePostcard()
            replay.focus({preventScroll: true})

            return
        }

        clearTimers()
        overlay.classList.remove('is-visible')
        overlay.classList.add('is-exiting')
        timers.push(setTimeout(() => {
            closePostcard()
            replay.focus({preventScroll: true})
        }, EXIT_MS))
    }

    const startPostcardMusic = () => {
        if (!ambienceButton || stopAmbience || !overlay.dataset.monthlyPostcardAudioSrc) return

        const stop = startAutumnJazz(overlay.dataset.monthlyPostcardAudioSrc, () => {
            if (stopAmbience !== stop) return
            stopAmbience = null
            ambienceButton.setAttribute('aria-pressed', 'false')
            ambienceButton.textContent = 'Play soft jazz'
        })
        stopAmbience = stop
        ambienceButton.setAttribute('aria-pressed', String(Boolean(stop)))
        ambienceButton.textContent = stop ? 'Mute music' : 'Play soft jazz'
    }

    const reveal = () => {
        const reducedMotion = prefersReducedMotion(overlay)
        document.body.append(overlay)
        overlay.hidden = false
        grid.inert = true
        // Give the unhidden, transparent overlay its own paint before starting the fade.
        if (!reducedMotion) void overlay.offsetWidth
        overlay.classList.add('is-visible')
        overlay.focus({preventScroll: true})
        startPostcardMusic()
        pageStage?.classList.add('is-fading')
        timers.push(setTimeout(() => {
            pageStage?.remove()
            pageStage = null
            document.body.classList.remove('monthly-postcard-page-melt')
            document.documentElement.classList.remove('monthly-postcard-page-playing')
        }, reducedMotion ? 0 : REVEAL_CROSSFADE_MS))
    }

    const startPageStage = () => {
        pageStage = document.createElement('div')
        pageStage.className = PAGE_STAGE_CLASS
        pageStage.dataset.motion = motion.name
        pageStage.dataset.theme = overlay.dataset.theme
        pageStage.dataset.edition = overlay.dataset.edition || ''
        pageStage.setAttribute('aria-hidden', 'true')

        for (let index = 0; index < 9; index += 1) {
            const ribbon = document.createElement('span')
            ribbon.className = 'monthly-postcard-page-stage__ribbon'
            ribbon.style.setProperty('--ribbon-index', index)
            pageStage.appendChild(ribbon)
        }

        if (october) addOctoberScenery(pageStage)

        const letter = document.createElement('div')
        letter.className = 'monthly-postcard-page-stage__letter'
        const month = document.createElement('strong')
        month.textContent = new Intl.DateTimeFormat('en-US', {month: 'long', timeZone: 'UTC'}).format(new Date(panel.dataset.monthlyPostcardMonth))
        const introduction = document.createElement('span')
        introduction.textContent = 'A little note for'
        const flourish = document.createElement('span')
        flourish.className = 'monthly-postcard-page-stage__flourish'
        flourish.textContent = '✿'
        letter.append(introduction, month, flourish)
        pageStage.appendChild(letter)

        document.body.appendChild(pageStage)
        document.body.dataset.monthlyPostcardPageMotion = motion.name
        document.body.style.setProperty('--postcard-page-duration', `${revealMs}ms`)
        document.documentElement.classList.add('monthly-postcard-page-playing')
        document.body.classList.add('monthly-postcard-page-melt')
    }

    const play = () => {
        if (playing || !disclosure.checked || (!preview && currentNewYorkMonth() !== panel.dataset.monthlyPostcardMonth)) {
            return
        }

        playing = true
        closePostcard()
        playing = true
        document.documentElement.classList.add('monthly-postcard-experience-active')
        panel.setAttribute('data-monthly-postcard-active', 'true')

        if (prefersReducedMotion(overlay)) {
            reveal()
            return
        }

        startPageStage()
        area.classList.add('monthly-postcard-rumble')
        timers.push(setTimeout(() => {
            area.classList.remove('monthly-postcard-rumble')
            area.classList.add('monthly-postcard-burst')
        }, rumbleMs))
        timers.push(setTimeout(reveal, revealMs))
    }

    const validMonth = () => {
        const valid = preview || currentNewYorkMonth() === panel.dataset.monthlyPostcardMonth
        replay.hidden = !valid

        if (!valid) {
            closePostcard()
        }

        return valid
    }

    closes.forEach((button) => button.addEventListener('click', () => {
        dismissPostcard()
    }, {signal: listeners.signal}))
    ambienceButton?.addEventListener('click', () => {
        if (stopAmbience) {
            stopAmbience()
            stopAmbience = null
        } else {
            startPostcardMusic()
        }
        ambienceButton.setAttribute('aria-pressed', String(Boolean(stopAmbience)))
        ambienceButton.textContent = stopAmbience ? 'Mute music' : 'Play soft jazz'
    }, {signal: listeners.signal})
    document.addEventListener('keydown', (event) => {
        if (playing && !overlay.hidden && 'Tab' === event.key) {
            const first = closes[0]
            const last = closes[closes.length - 1]
            if ((!event.shiftKey && (document.activeElement === last || document.activeElement === overlay)) ||
                (event.shiftKey && (document.activeElement === first || document.activeElement === overlay))) {
                event.preventDefault()
                const target = event.shiftKey ? last : first
                target.focus({preventScroll: true})
            }
        }

        if (playing && 'Escape' === event.key) {
            dismissPostcard()
        }
    }, {signal: listeners.signal})
    disclosure.addEventListener('change', () => {
        if (!disclosure.checked) closePostcard()
    }, {signal: listeners.signal})
    document.addEventListener('livewire:navigating', closePostcard, {signal: listeners.signal})
    window.addEventListener('pagehide', closePostcard, {signal: listeners.signal})
    document.addEventListener('visibilitychange', validMonth, {signal: listeners.signal})

    replay.addEventListener('click', async () => {
        if (pending || !validMonth()) return

        if (preview) {
            play()

            return
        }

        pending = true
        replay.disabled = true
        const recorded = await postPostcardEvent(panel.dataset.monthlyPostcardReplayEndpoint)
        pending = false
        replay.disabled = false
        if (recorded) play()
    }, {signal: listeners.signal})

    if ('true' === panel.dataset.monthlyPostcardAutoStart && validMonth()) {
        if (preview) {
            play()

            return
        }

        const startWhenVisible = async () => {
            if (document.visibilityState !== 'visible' || !disclosure.checked || !validMonth()) return

            document.removeEventListener('visibilitychange', startWhenVisible)
            const result = await postPostcardEvent(panel.dataset.monthlyPostcardStartEndpoint)

            if (result?.play) play()
        }

        document.addEventListener('visibilitychange', startWhenVisible, {signal: listeners.signal})
        startWhenVisible()
    }
}

function initializePostcards(root = document) {
    const ancestor = root.closest?.(PANEL_SELECTOR)
    if (ancestor) initializePostcard(ancestor)
    if (root.matches?.(PANEL_SELECTOR)) initializePostcard(root)
    root.querySelectorAll?.(PANEL_SELECTOR).forEach(initializePostcard)
}

if (typeof document !== 'undefined') {
    document.addEventListener('DOMContentLoaded', () => initializePostcards())
    document.addEventListener('livewire:navigated', () => initializePostcards())
    new MutationObserver((mutations) => {
        mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) initializePostcards(node)
        }))
    }).observe(document.documentElement, {childList: true, subtree: true})
}
